import { Component } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { SpeedTestService } from '../services/speed-test.service';

interface SpeedTestStats {
  totalMessages: number;
  elapsedMs: number;
  messagesPerSecond: number;
}

@Component({
  selector: 'app-speed-test-overlay',
  template: `
    <div class="speed-test-box" *ngIf="speedTestService.isSpeedTestEnabled$ | async">
      <div class="speed-test-header">
        <span>⚡ Speed Test</span>
        <button class="reset-btn" (click)="onReset()">Reset</button>
      </div>
      <ng-container *ngIf="stats$ | async as stats">
        <div class="stat-row">
          <span class="stat-label">Time</span>
          <span class="stat-value">{{ formatTime(stats.elapsedMs) }}</span>
        </div>
        <div class="stat-row">
          <span class="stat-label">Sent</span>
          <span class="stat-value">{{ stats.totalMessages }}</span>
        </div>
        <div class="stat-row">
          <span class="stat-label">Msgs/s</span>
          <span class="stat-value">{{ stats.messagesPerSecond | number: '1.1-1' }}</span>
        </div>
      </ng-container>
    </div>
  `,
  styles: [
    `
      .speed-test-box {
        position: fixed;
        top: 12px;
        right: 12px;
        z-index: 1040;
        background: rgba(0, 0, 0, 0.88);
        color: #fff;
        font-family: 'SF Mono', 'Menlo', 'Monaco', 'Consolas', monospace;
        font-size: 12px;
        padding: 10px 14px;
        border-radius: 8px;
        min-width: 170px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        user-select: none;
      }

      .speed-test-header {
        display: flex;
        justify-content: space-between;
        gap: 16px;
        align-items: center;
        margin-bottom: 8px;
        font-weight: 600;
        font-size: 13px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.15);
        padding-bottom: 6px;
      }

      .reset-btn {
        background: rgba(255, 255, 255, 0.15);
        color: #fff;
        border: none;
        border-radius: 4px;
        padding: 2px 8px;
        font-size: 11px;
        cursor: pointer;
        font-family: inherit;
        transition: background 0.15s;
      }

      .reset-btn:hover {
        background: rgba(255, 255, 255, 0.3);
      }

      .stat-row {
        display: flex;
        justify-content: space-between;
        padding: 2px 0;
      }

      .stat-label {
        color: rgba(255, 255, 255, 0.6);
      }

      .stat-value {
        font-weight: 500;
      }
    `
  ]
})
export class SpeedTestOverlayComponent {
  stats$: Observable<SpeedTestStats>;

  constructor(public speedTestService: SpeedTestService) {
    this.stats$ = combineLatest([
      this.speedTestService.totalMessages$,
      this.speedTestService.elapsedMs$,
      this.speedTestService.messagesPerSecond$
    ]).pipe(
      map(([totalMessages, elapsedMs, messagesPerSecond]) => ({
        totalMessages,
        elapsedMs,
        messagesPerSecond
      }))
    );
  }

  onReset(): void {
    this.speedTestService.resetStats();
  }

  formatTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const tenths = Math.floor((ms % 1000) / 100);
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');
    return `${mm}:${ss}.${tenths}`;
  }
}
